import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";
import Icon from "../components/Icon";
import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

import UserStarIcon from "../assets/icons/user-star.png";
import EyeIcon from "../assets/icons/Eye.png";
import UserIcon from "../assets/icons/Account.png";
import CalendarIcon from "../assets/icons/Planner.png";
import DealIcon from "../assets/icons/Discount.png";
import RequestIcon from "../assets/icons/Communication.png";
import SpinAndWinIcon from "../assets/icons/Community ticket.png";
import SearchIcon from "../assets/icons/Search.png";
import WinTicketsIcon from "../assets/icons/Community ticket.png";

// Helper function to calculate date range
const getDateRange = (range, customStart = null, customEnd = null) => {
  const now = new Date();
  let startDate, endDate;

  if (range === "CUSTOM" && customStart && customEnd) {
    startDate = new Date(customStart);
    endDate = new Date(customEnd);
    // Set end date to end of day
    endDate.setHours(23, 59, 59, 999);
  } else {
    endDate = now;
    switch (range) {
      case "1D":
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "7D":
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "30D":
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

// Helper function to calculate previous period date range
const getPreviousPeriodDateRange = (range, customStart = null, customEnd = null) => {
  if (range === "CUSTOM" && customStart && customEnd) {
    const start = new Date(customStart);
    const end = new Date(customEnd);
    const duration = end.getTime() - start.getTime();

    const prevEnd = new Date(start.getTime() - 1); // Day before current start
    const prevStart = new Date(prevEnd.getTime() - duration);

    return {
      startDate: prevStart.toISOString(),
      endDate: prevEnd.toISOString(),
    };
  } else {
    const now = new Date();
    let currentStart, currentEnd, prevStart, prevEnd;

    currentEnd = now;
    switch (range) {
      case "1D":
        currentStart = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        prevEnd = new Date(currentStart.getTime() - 1);
        prevStart = new Date(prevEnd.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "7D":
        currentStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        prevEnd = new Date(currentStart.getTime() - 1);
        prevStart = new Date(prevEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "30D":
        currentStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        prevEnd = new Date(currentStart.getTime() - 1);
        prevStart = new Date(prevEnd.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        currentStart = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        prevEnd = new Date(currentStart.getTime() - 1);
        prevStart = new Date(prevEnd.getTime() - 24 * 60 * 60 * 1000);
    }

    return {
      startDate: prevStart.toISOString(),
      endDate: prevEnd.toISOString(),
    };
  }
};

// Helper function to calculate percentage change
const calculatePercentageChange = (current, previous) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
};



const NumberAnalyticsCard = ({
  title,
  value,
  increase,
  explanation,
  icon,
  loading,
}) => {
  return (
    <AnalyticsCard
      height={176}
      icon={icon}
      title={title}
      explanation={explanation}
      loading={loading}
    >
      <div className="mt-[16px]">
        <div className="font-semibold text-[40px] text-white leading-[44px] tracking-[-0.5px]">
          {loading ? "..." : value}
        </div>
      </div>
      <div
        className={`flex justify-center items-center mt-[16px] px-[16px] border rounded-full w-fit h-[32px] tracking-[0px] ${
          increase >= 0
            ? "border-green-500 text-green-100"
            : "border-red-500 text-red-100"
        }`}
      >
        {loading
          ? "..."
          : `${increase}% ${increase >= 0 ? "increase" : "decrease"}`}
      </div>
    </AnalyticsCard>
  );
};

const ComparisonAnalyticsCard = ({
  title,
  value,
  totalValue,
  explanation,
  icon,
  loading,
}) => {
  // Icon mapping for each page
  const pageIcons = {
    EVENTS: CalendarIcon,
    DEALS: DealIcon,
    COMMUNITY_CHATS: RequestIcon,
    ACCOUNT: UserIcon,
    SPIN_WIN: SpinAndWinIcon,
  };

  // Convert value object to array and sort from high to low
  const sortedData = Object.entries(value || {})
    .sort(([, a], [, b]) => b - a)
    .map(([page, count]) => ({
      page: page
        .replace("_", " ")
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase()),
      count,
      icon: pageIcons[page] || SearchIcon,
    }));

  return (
    <AnalyticsCard
      height={258}
      icon={icon}
      title={title}
      explanation={explanation}
      loading={loading}
    >
      <div className="mt-[12px] font-semibold text-[40px] text-white leading-[44px] tracking-[-0.5px]">
        {loading ? "..." : totalValue}
      </div>
      <div className="space-y-[6px] mt-[12px] max-h-[132px] overflow-y-auto">
        {loading ? (
          <div className="text-neutral-400">Loading...</div>
        ) : (
          sortedData.map(({ page, count, icon: pageIcon }) => (
            <div
              key={page}
              className="flex justify-between items-center bg-[#0B0415] px-[12px] py-[6px] rounded-[8px]"
            >
              <div className="flex items-center gap-[8px]">
                <img src={pageIcon} alt={page} className="w-[18px] h-[18px]" />
                <span className="text-[16px] text-neutral-200">{page}</span>
              </div>
              <span className="font-semibold text-[#C9A6F5] text-[18px]">
                {count}
              </span>
            </div>
          ))
        )}
      </div>
    </AnalyticsCard>
  );
};

const PieChartAnalyticsCard = ({
  title,
  value,
  explanation,
  icon,
  loading,
}) => {
  const colors = ["#FDBC64", "#999BEC", "#F2565F", "#924DEA"];

  // Convert value object to array and calculate total
  const data = Object.entries(value || {}).map(([label, count], index) => ({
    label,
    count,
    color: colors[index % colors.length],
  }));

  const total = data.reduce((sum, item) => sum + item.count, 0);

  // Calculate angles for pie chart
  let currentAngle = 0;
  const segments = data.map((item) => {
    const angle = (item.count / total) * 360;
    const segment = {
      ...item,
      startAngle: currentAngle,
      endAngle: currentAngle + angle,
    };
    currentAngle += angle;
    return segment;
  });

  return (
    <AnalyticsCard
      height={300}
      icon={icon}
      title={title}
      explanation={explanation}
      loading={loading}
    >
      <div className="flex flex-col justify-center items-center h-full">
        {loading ? (
          <div className="text-neutral-400">Loading...</div>
        ) : (
          <>
            <div className="relative flex justify-center items-center">
              {/* Donut Chart */}
              <svg width="120" height="120" className="-rotate-90 transform">
                {segments.map((segment, index) => {
                  const radius = 50;
                  const centerX = 60;
                  const centerY = 60;

                  const startAngleRad = (segment.startAngle * Math.PI) / 180;
                  const endAngleRad = (segment.endAngle * Math.PI) / 180;

                  const x1 = centerX + radius * Math.cos(startAngleRad);
                  const y1 = centerY + radius * Math.sin(startAngleRad);
                  const x2 = centerX + radius * Math.cos(endAngleRad);
                  const y2 = centerY + radius * Math.sin(endAngleRad);

                  const largeArcFlag =
                    segment.endAngle - segment.startAngle > 180 ? 1 : 0;

                  const pathData = [
                    `M ${x1} ${y1}`,
                    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                  ].join(" ");

                  return (
                    <path
                      key={index}
                      d={pathData}
                      fill="none"
                      stroke={segment.color}
                      strokeWidth="8"
                      strokeLinecap="round"
                    />
                  );
                })}
              </svg>

              {/* Center Total */}
              <div className="absolute font-semibold text-[26px] text-white">
                {total}
              </div>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-[12px] mt-[16px]">
              {data.map((item, index) => {
                const percentage =
                  total > 0 ? Math.round((item.count / total) * 100) : 0;
                return (
                  <div key={index} className="flex items-center gap-[6px]">
                    <div
                      className="rounded-full w-[8px] h-[8px]"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-[12px] text-white">
                      {item.label} ({percentage}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </AnalyticsCard>
  );
};

const BarChartAnalyticsCard = ({
  title,
  value,
  totalValue,
  explanation,
  icon,
  loading,
}) => {
  // Icon mapping for each page
  const pageIcons = {
    Events: CalendarIcon,
    Deals: DealIcon,
    Community: RequestIcon,
    Account: UserIcon,
    SpinWin: SpinAndWinIcon,
  };

  // Convert value object to array and sort from high to low
  const sortedData = Object.entries(value || {})
    .sort(([, a], [, b]) => b - a)
    .map(([page, count]) => ({
      page,
      count,
      icon: pageIcons[page] || SearchIcon,
    }));

  return (
    <AnalyticsCard
      height={300}
      icon={icon}
      title={title}
      explanation={explanation}
      loading={loading}
    >
      <div className="mt-[12px] font-semibold text-[40px] text-white leading-[44px] tracking-[-0.5px]">
        {loading ? "..." : `${totalValue}%`}
      </div>
      <div className="space-y-[16px] mt-[20px] max-h-[140px] overflow-y-auto">
        {loading ? (
          <div className="text-neutral-400">Loading...</div>
        ) : (
          sortedData.map(({ page, count, icon: pageIcon }, idx) => {
            // Find the max value for bar width calculation
            const maxCount = 100;
            // Bar color palette (can be expanded)
            const barColors = [
              "#B98AFF", // purple
              "#7B5AFF", // blueish
              "#C9A6F5", // light purple
              "#A3A3A3", // gray
              "#F5C6A6", // peach
            ];
            const barColor = barColors[idx % barColors.length];
            return (
              <div key={page} className="flex flex-col gap-[4px] rounded-[8px]">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-[8px]">
                    <div
                      className="rounded-full w-[10px] h-[10px]"
                      style={{ backgroundColor: barColor }}
                    />
                    <span className="text-[15px] text-neutral-200">{page}</span>
                  </div>
                  <span className="font-semibold text-[16px] text-white">
                    {count}%
                  </span>
                </div>
                <div className="bg-[#25202E] mt-[2px] rounded-[4px] w-full h-[6px] overflow-hidden">
                  <div
                    className="rounded-[4px] h-full transition-all duration-300"
                    style={{
                      width: `${(count / maxCount) * 100}%`,
                      background: barColor,
                    }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </AnalyticsCard>
  );
};

const AnalyticsCard = ({
  icon,
  title,
  explanation,
  children,
  height,
  loading,
}) => {
  return (
    <div
      className="bg-[#17131F] p-[24px] border border-[#25202E] rounded-[8px]"
      style={{ height: `${height}px` }}
    >
      <div className="flex items-center gap-[8px]">
        <img src={icon} alt={title} className="w-[20px]" />
        <div className="font-semibold text-[16px] text-neutral-400 leading-[20px]">
          {title}
        </div>
        <div className="group relative">
          <Icon
            icon="heroicons:information-circle"
            className="w-[16px] h-[16px] text-neutral-100 cursor-help"
          />
          <div className="bottom-full left-1/2 z-10 absolute bg-gray-800 opacity-0 group-hover:opacity-100 mb-2 px-3 py-2 rounded-lg text-[14px] text-white whitespace-nowrap transition-opacity -translate-x-1/2 duration-200 transform">
            {explanation}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

export default function AdminAnalytics() {
  const user = useSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    totalUniqueVisitors: 0,
    totalUniqueVisitorsChange: 0,
    avgSignupsPerUser: 0,
    avgSignupsPerUserChange: 0,
    viewsPerPage: {},
    totalPageViews: 0,
    searchQueries: {},
    totalSearches: 0,
    filters: {},
    totalFilters: 0,
    conversionRatePerEvent: {},
    totalConversionRate: 0,
    totalActiveUsers: 0,
    totalActiveUsersChange: 0,
    returningVsNewCustomers: {
      returning: 0,
      new: 0,
    },
    newAccountsCreated: 0,
    newAccountsCreatedChange: 0,
  });

  const [dateRange, setDateRange] = useState("1D");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showCustomDatePickers, setShowCustomDatePickers] = useState(false);

  const handleCustomDateRange = (startDate, endDate) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    setDateRange("CUSTOM");
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (range === "CUSTOM") {
      setShowCustomDatePickers(true);
      // Set default dates
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setCustomStartDate(thirtyDaysAgo.toISOString().split("T")[0]);
      setCustomEndDate(now.toISOString().split("T")[0]);
    } else {
      setShowCustomDatePickers(false);
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);

        // Get date range for filtering
        const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

        // 1. Total unique visitors (PAGE_VIEW with value GENERAL grouped by user_cookie)
        const { data: uniqueVisitors, error: visitorsError } = await supabase
          .from("PageEvent")
          .select("user_cookie")
          .eq("type", "PAGE_VIEW")
          .eq("value", "GENERAL")
          .gte("created_at", startDate)
          .lte("created_at", endDate);

        if (visitorsError) throw visitorsError;

        const uniqueVisitorCount = new Set(
          uniqueVisitors.map((v) => v.user_cookie)
        ).size;

        // 2. Average signups per user (EventSignUp grouped by email)
        const { data: signups, error: signupsError } = await supabase
          .from("EventSignUp")
          .select("email")
          .gte("created_at", startDate)
          .lte("created_at", endDate);

        if (signupsError) throw signupsError;

        const emailSignupCounts = signups.reduce((acc, signup) => {
          acc[signup.email] = (acc[signup.email] || 0) + 1;
          return acc;
        }, {});

        const avgSignupsPerUser =
          Object.keys(emailSignupCounts).length > 0
            ? Object.values(emailSignupCounts).reduce((a, b) => a + b, 0) /
              Object.keys(emailSignupCounts).length
            : 0;

        // 3. Views per page (PAGE_VIEW grouped by value, only 1 entry per user_cookie)
        const { data: pageViews, error: pageViewsError } = await supabase
          .from("PageEvent")
          .select("value, user_cookie")
          .eq("type", "PAGE_VIEW")
          .neq("value", "GENERAL")
          .gte("created_at", startDate)
          .lte("created_at", endDate);

        if (pageViewsError) throw pageViewsError;

        // Group by value and user_cookie, then count unique user_cookies per value
        const pageViewCounts = pageViews.reduce((acc, view) => {
          if (!acc[view.value]) {
            acc[view.value] = new Set();
          }
          acc[view.value].add(view.user_cookie);
          return acc;
        }, {});

        const viewsPerPage = Object.keys(pageViewCounts).reduce((acc, page) => {
          acc[page] = pageViewCounts[page].size;
          return acc;
        }, {});

        const totalPageViews = Object.values(viewsPerPage).reduce(
          (a, b) => a + b,
          0
        );

        // 4. Most popular search queries (SEARCH_QUERY grouped by value, only 1 entry per user_cookie)
        const { data: searches, error: searchesError } = await supabase
          .from("PageEvent")
          .select("value, user_cookie")
          .eq("type", "SEARCH_QUERY")
          .gte("created_at", startDate)
          .lte("created_at", endDate);

        if (searchesError) throw searchesError;

        // Group by value and user_cookie, then count unique user_cookies per value
        const searchCounts = searches.reduce((acc, search) => {
          if (!acc[search.value]) {
            acc[search.value] = new Set();
          }
          acc[search.value].add(search.user_cookie);
          return acc;
        }, {});

        const searchQueries = Object.keys(searchCounts).reduce((acc, query) => {
          acc[query] = searchCounts[query].size;
          return acc;
        }, {});

        const totalSearches = Object.values(searchQueries).reduce(
          (a, b) => a + b,
          0
        );

        // 5. Most popular filters (FILTER type grouped by value, only 1 entry per user_cookie)
        const { data: filterData, error: filtersError } = await supabase
          .from("PageEvent")
          .select("value, user_cookie")
          .eq("type", "FILTER")
          .gte("created_at", startDate)
          .lte("created_at", endDate);

        if (filtersError) throw filtersError;

        // Group by value and user_cookie, then count unique user_cookies per value
        const filterCounts = filterData.reduce((acc, filter) => {
          if (!acc[filter.value]) {
            acc[filter.value] = new Set();
          }
          acc[filter.value].add(filter.user_cookie);
          return acc;
        }, {});

        const filters = Object.keys(filterCounts).reduce((acc, filterType) => {
          acc[filterType] = filterCounts[filterType].size;
          return acc;
        }, {});

        const totalFilters = Object.values(filters).reduce((a, b) => a + b, 0);

        // 6. Conversion rate per event (signups / event views)
        const { data: eventViews, error: eventViewsError } = await supabase
          .from("PageEvent")
          .select("value")
          .eq("type", "EVENT_VIEW")
          .gte("created_at", startDate)
          .lte("created_at", endDate);

        if (eventViewsError) throw eventViewsError;

        const { data: eventSignups, error: eventSignupsError } = await supabase
          .from("EventSignUp")
          .select("event_id")
          .gte("created_at", startDate)
          .lte("created_at", endDate);

        if (eventSignupsError) throw eventSignupsError;

        // Get event names for the events that have views
        const eventIds = [...new Set(eventViews.map((view) => String(view.value)))];
        const { data: events, error: eventsError } = await supabase
          .from("Events")
          .select("id, name")
          .in("id", eventIds);

        if (eventsError) throw eventsError;

        // Create event name lookup
        const eventNameLookup = events.reduce((acc, event) => {
          const eventId = String(event.id); // Convert to string for consistency
          acc[eventId] = event.name;
          return acc;
        }, {});

        // Calculate conversion rate per event
        const eventViewCounts = eventViews.reduce((acc, view) => {
          const eventId = String(view.value); // Convert to string for consistency
          acc[eventId] = (acc[eventId] || 0) + 1;
          return acc;
        }, {});

        const eventSignupCounts = eventSignups.reduce((acc, signup) => {
          const eventId = String(signup.event_id); // Convert to string for consistency
          acc[eventId] = (acc[eventId] || 0) + 1;
          return acc;
        }, {});

        const conversionRatePerEvent = {};
        let totalConversionRate = 0;
        let eventCount = 0;

        // Debug: Log the counts to verify data matching
        console.log("Event View Counts:", eventViewCounts);
        console.log("Event Signup Counts:", eventSignupCounts);

        Object.keys(eventViewCounts).forEach((eventId) => {
          const views = eventViewCounts[eventId];
          const signups = eventSignupCounts[eventId] || 0;
          if (views > 0) {
            const conversionRate = (signups / views) * 100;
            const eventName = eventNameLookup[eventId] || `Event ${eventId}`;
            conversionRatePerEvent[eventName] =
              Math.round(conversionRate * 100) / 100;
            totalConversionRate += conversionRate;
            eventCount++;

            // Debug: Log individual conversion rates
            console.log(`Event ${eventName} (ID: ${eventId}): ${views} views, ${signups} signups, ${conversionRate.toFixed(2)}% conversion`);
          }
        });

        // Sort conversion rates and take top 10
        const sortedConversionRates = Object.entries(conversionRatePerEvent)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 10)
          .reduce((acc, [eventName, rate]) => {
            acc[eventName] = rate;
            return acc;
          }, {});

        const avgConversionRate =
          eventCount > 0 ? totalConversionRate / eventCount : 0;

        // 7. Total active users (unique emails from EventSignUp)
        const { data: allSignups, error: allSignupsError } = await supabase
          .from("EventSignUp")
          .select("email")
          .gte("created_at", startDate)
          .lte("created_at", endDate);

        if (allSignupsError) throw allSignupsError;

        const uniqueEmails = new Set(allSignups.map((signup) => signup.email));
        const totalActiveUsers = uniqueEmails.size;

        // 8. Returning vs new customers (first signup per email)
        const { data: signupsWithDates, error: signupsWithDatesError } =
          await supabase
            .from("EventSignUp")
            .select("email, created_at")
            .gte("created_at", startDate)
            .lte("created_at", endDate)
            .order("created_at", { ascending: true });

        if (signupsWithDatesError) throw signupsWithDatesError;

        const firstSignupPerEmail = {};
        signupsWithDates.forEach((signup) => {
          if (!firstSignupPerEmail[signup.email]) {
            firstSignupPerEmail[signup.email] = signup.created_at;
          }
        });

        const newCustomers = Object.keys(firstSignupPerEmail).length;
        const totalSignups = allSignups.length;
        const returningCustomers = totalSignups - newCustomers;

        // 9. New accounts created (unique emails from User table)
        const { data: users, error: usersError } = await supabase
          .from("User")
          .select("email")
          .gte("created_at", startDate)
          .lte("created_at", endDate);

        if (usersError) throw usersError;

        const uniqueUserEmails = new Set(users.map((user) => user.email));
        const newAccountsCreated = uniqueUserEmails.size;

        // ========== PREVIOUS PERIOD CALCULATIONS ==========
        const { startDate: prevStartDate, endDate: prevEndDate } = getPreviousPeriodDateRange(dateRange, customStartDate, customEndDate);

        // Previous period: Total unique visitors
        const { data: prevUniqueVisitors } = await supabase
          .from("PageEvent")
          .select("user_cookie")
          .eq("type", "PAGE_VIEW")
          .eq("value", "GENERAL")
          .gte("created_at", prevStartDate)
          .lte("created_at", prevEndDate);

        const prevUniqueVisitorCount = new Set(
          (prevUniqueVisitors || []).map((v) => v.user_cookie)
        ).size;

        // Previous period: Average signups per user
        const { data: prevSignups } = await supabase
          .from("EventSignUp")
          .select("email")
          .gte("created_at", prevStartDate)
          .lte("created_at", prevEndDate);

        const prevEmailSignupCounts = (prevSignups || []).reduce((acc, signup) => {
          acc[signup.email] = (acc[signup.email] || 0) + 1;
          return acc;
        }, {});

        const prevAvgSignupsPerUser =
          Object.keys(prevEmailSignupCounts).length > 0
            ? Object.values(prevEmailSignupCounts).reduce((a, b) => a + b, 0) /
              Object.keys(prevEmailSignupCounts).length
            : 0;

        // Previous period: Total active users
        const { data: prevAllSignups } = await supabase
          .from("EventSignUp")
          .select("email")
          .gte("created_at", prevStartDate)
          .lte("created_at", prevEndDate);

        const prevUniqueEmails = new Set((prevAllSignups || []).map((signup) => signup.email));
        const prevTotalActiveUsers = prevUniqueEmails.size;

        // Previous period: New accounts created
        const { data: prevUsers } = await supabase
          .from("User")
          .select("email")
          .gte("created_at", prevStartDate)
          .lte("created_at", prevEndDate);

        const prevUniqueUserEmails = new Set((prevUsers || []).map((user) => user.email));
        const prevNewAccountsCreated = prevUniqueUserEmails.size;

        // Calculate percentage changes
        const totalUniqueVisitorsChange = calculatePercentageChange(uniqueVisitorCount, prevUniqueVisitorCount);
        const avgSignupsPerUserChange = calculatePercentageChange(avgSignupsPerUser, prevAvgSignupsPerUser);
        const totalActiveUsersChange = calculatePercentageChange(totalActiveUsers, prevTotalActiveUsers);
        const newAccountsCreatedChange = calculatePercentageChange(newAccountsCreated, prevNewAccountsCreated);

        setAnalytics({
          totalUniqueVisitors: uniqueVisitorCount,
          totalUniqueVisitorsChange,
          avgSignupsPerUser: Math.round(avgSignupsPerUser * 100) / 100,
          avgSignupsPerUserChange,
          viewsPerPage,
          totalPageViews,
          searchQueries,
          totalSearches,
          filters,
          totalFilters,
          conversionRatePerEvent: sortedConversionRates,
          totalConversionRate: Math.round(avgConversionRate * 100) / 100,
          totalActiveUsers,
          totalActiveUsersChange,
          returningVsNewCustomers: {
            returning: returningCustomers,
            new: newCustomers,
          },
          newAccountsCreated,
          newAccountsCreatedChange,
        });
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [dateRange, customStartDate, customEndDate]);

  return (
    <div className="flex justify-center items-center w-full min-h-calc(100vh-60px)">
      <div className="lg:px-[48px] py-[40px] w-[1200px] max-w-[95vw]">
        {/* Header with date filter */}
        <div className="flex lg:flex-row flex-col justify-between lg:items-center w-full">
          <div>
            <div className="font-semibold text-[26px] text-white leading-[32px]">
              Hi {user.first_name} {user.last_name}
            </div>
            <div className="mt-[4px] text-[16px] text-neutral-100 leading-[22px]">
              Welcome to your dashboard
            </div>
          </div>
          <div className="flex justify-start items-center gap-[12px] max-lg:mt-[12px]">
            <div className="flex items-center gap-[8px] bg-[#17131F] p-[8px] rounded-[8px]">
              <button
                onClick={() => handleDateRangeChange("1D")}
                className={`${
                  dateRange === "1D" ? "bg-[#7721E5]" : ""
                } text-white font-medium rounded-[6px] py-[6px] px-[8px]`}
              >
                1D
              </button>
              <button
                onClick={() => handleDateRangeChange("7D")}
                className={`${
                  dateRange === "7D" ? "bg-[#7721E5]" : ""
                } text-white font-medium rounded-[6px] py-[6px] px-[8px]`}
              >
                7D
              </button>
              <button
                onClick={() => handleDateRangeChange("30D")}
                className={`${
                  dateRange === "30D" ? "bg-[#7721E5]" : ""
                } text-white font-medium rounded-[6px] py-[6px] px-[8px]`}
              >
                30D
              </button>
              <button
                onClick={() => handleDateRangeChange("CUSTOM")}
                className={`${
                  dateRange === "CUSTOM" ? "bg-[#7721E5]" : ""
                } text-white font-medium rounded-[6px] py-[6px] px-[8px]`}
              >
                Custom
              </button>
            </div>
          </div>
        </div>

        {/* Custom Date Picker - Inline */}
        {showCustomDatePickers && (
          <div className="bg-[#17131F] mt-4 p-4 rounded-lg">
            <div className="flex items-center gap-4 mb-4">
              <h3 className="font-semibold text-white">Custom Date Range</h3>
              <button
                onClick={() => setShowCustomDatePickers(false)}
                className="text-gray-400 hover:text-white"
              >
                <Icon icon="heroicons:x-mark" className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block mb-2 font-medium text-gray-300 text-sm">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate || ""}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-[#0B0415] p-2 border border-gray-600 focus:border-purple-500 rounded-md focus:outline-none w-full text-white"
                />
              </div>
              <div className="flex-1">
                <label className="block mb-2 font-medium text-gray-300 text-sm">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate || ""}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-[#0B0415] p-2 border border-gray-600 focus:border-purple-500 rounded-md focus:outline-none w-full text-white"
                />
              </div>
            </div>
          </div>
        )}

        {/* Analytics cards */}
        <div className="gap-[16px] grid grid-cols-1 lg:grid-cols-2 mt-[48px]">
          <NumberAnalyticsCard
            icon={UserStarIcon}
            title="Total unique visitors"
            value={analytics.totalUniqueVisitors}
            increase={analytics.totalUniqueVisitorsChange}
            explanation="The total amount of people that visited the platform"
            loading={loading}
          />
          <NumberAnalyticsCard
            icon={EyeIcon}
            title="Avg. signups per user"
            value={analytics.avgSignupsPerUser}
            increase={analytics.avgSignupsPerUserChange}
            explanation="How many times every user signed up for an event"
            loading={loading}
          />
          <ComparisonAnalyticsCard
            totalValue={analytics.totalPageViews}
            icon={EyeIcon}
            title="Views per page"
            value={analytics.viewsPerPage}
            explanation="How many times each page was viewed"
            loading={loading}
          />
          <ComparisonAnalyticsCard
            totalValue={analytics.totalSearches}
            icon={SearchIcon}
            title="Most popular search queries"
            value={analytics.searchQueries}
            explanation="How many times people searched for these events"
            loading={loading}
          />
          <PieChartAnalyticsCard
            totalValue={analytics.totalFilters}
            icon={SearchIcon}
            title="Most popular filters"
            value={analytics.filters}
            explanation="How many times people used these filters"
            loading={loading}
          />
          <BarChartAnalyticsCard
            totalValue={analytics.totalConversionRate}
            icon={WinTicketsIcon}
            title="Conversion rate per event"
            value={analytics.conversionRatePerEvent}
            explanation="The percentage of users that viewed the event and clicked the signup button"
            loading={loading}
          />
          <PieChartAnalyticsCard
            icon={WinTicketsIcon}
            title="Sharing behaviour"
            value={{
              Whatsapp: 0,
              Socials: 0,
              "Community link": 0,
              "Share button": 0,
            }}
            explanation="Nog onduidelijk"
            loading={loading}
          />
          <PieChartAnalyticsCard
            icon={WinTicketsIcon}
            title="Returning vs new customers"
            value={{
              Returning: analytics.returningVsNewCustomers.returning,
              New: analytics.returningVsNewCustomers.new,
            }}
            explanation="A new customer is someone that has never signed up for an event before"
            loading={loading}
          />
          <NumberAnalyticsCard
            icon={UserStarIcon}
            title="Total active users"
            value={analytics.totalActiveUsers}
            increase={analytics.totalActiveUsersChange}
            explanation="The total amount of people that have signed up for an event"
            loading={loading}
          />
          <NumberAnalyticsCard
            icon={UserStarIcon}
            title="New accounts created"
            value={analytics.newAccountsCreated}
            increase={analytics.newAccountsCreatedChange}
            explanation="The total amount of people that have created an account"
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}
